// src/components/simulation/PretextingSimulator.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Phone, PhoneOff, AlertTriangle, CheckCircle, X, BookOpen } from 'lucide-react';

const getDefaultScenario = () => ({
  title: 'Impersonation Call from "IT Support"',
  description: "You receive an unexpected phone call from someone claiming to be from your company's IT Support department.",
  initialSceneId: 'start',
  arenaId: 'phishing_social_engineering',
  scenes: {
    'start': {
      speaker: 'Caller (IT Support)',
      line: "Hello, this is IT Support. We've detected a critical virus on your machine and need to gain remote access immediately. Please go to www.fastsupport.net and give me the access code.",
      choices: [
        { text: "Okay, I'll go to the site now.", nextSceneId: 'fail_compliance' },
        { text: "How can I verify you're really from IT?", nextSceneId: 'correct_verify' }
      ]
    },
    'fail_compliance': {
      speaker: 'Caller',
      line: "Great, I have the code... and I'm in. We'll take it from here.",
      isEndState: true,
      isCorrect: false,
      explanation: "Complying with an unverified, urgent request from an unknown caller is extremely risky. You should always verify the person's identity through a separate, trusted channel before taking any action.",
      indicators: ["Creates a sense of urgency", "Asks for immediate action without verification", "Directs you to an unfamiliar website"]
    },
    'correct_verify': {
      speaker: 'Caller',
      line: "There's no time for that! This is an emergency! *click*",
      isEndState: true,
      isCorrect: true,
      explanation: "Excellent! You correctly questioned the caller's identity instead of immediately complying. This is a crucial step in preventing pretexting attacks. The caller's evasive and pressuring response confirms the suspicion."
    }
  }
});

const PretextingSimulator = ({ scenarios = [], onComplete, challenge }) => {
  const [scenario, setScenario] = useState(null);
  const [history, setHistory] = useState([]);
  const [currentScene, setCurrentScene] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [callState, setCallState] = useState('ringing');
  const [timer, setTimer] = useState(0);
  const [finalFeedback, setFinalFeedback] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const chatEndRef = useRef(null);
  const navigate = useNavigate();

  // --- Voice & Audio State ---
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [attackerVoice, setAttackerVoice] = useState(null);
  const [userVoice, setUserVoice] = useState(null);
  const [voiceUnlocked, setVoiceUnlocked] = useState(false);

  // Ringtone refs
  const audioCtxRef = useRef(null);
  const oscillatorRef = useRef(null);
  const gainRef = useRef(null);
  const ringIntervalRef = useRef(null);
  const ringTimeoutsRef = useRef([]);

  // Voice preferences
  const ATTACKER_VOICE_NAME = 'Ravi';
  const USER_VOICE_NAME = 'Heera';

  // Load voices
  useEffect(() => {
    const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;
    setVoiceSupported(supported);
    if (!supported) return;

    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length === 0) return;

      const attacker = voices.find(v => v.name.includes(ATTACKER_VOICE_NAME)) ||
        voices.find(v => v.lang.startsWith('en-IN')) ||
        voices.find(v => v.lang.includes('en')) ||
        voices[0];

      const user = voices.find(v => v.name.includes(USER_VOICE_NAME)) ||
        voices.find(v => v.lang.startsWith('en-US')) ||
        voices.find(v => v.lang.includes('en')) ||
        voices[0];

      setAttackerVoice(attacker);
      setUserVoice(user);
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  // --- Scenario Setup ---
  // useEffect(() => {
  //   const validScenarios = scenarios.length > 0 && scenarios[0].scenes ? scenarios : [getDefaultScenario()];
  //   const init = validScenarios[0];
  //   setScenario(init);
  //   setCurrentScene(init.scenes[init.initialSceneId || 'start']);

  //   // Reset everything
  //   setHistory([]);
  //   setIsComplete(false);
  //   setCallState('ringing');
  //   setTimer(0);
  //   setFinalFeedback(null);
  //   setShowFeedbackModal(false);
  // }, [scenarios]);

  // --- Scenario Setup ---
  useEffect(() => {
    let initScenario = null;

    // Priority 1: If scenarios is array and first item has .scenes → old format
    if (scenarios && Array.isArray(scenarios) && scenarios.length > 0) {
      const first = scenarios[0];

      // Old format: scenarios[0] is full scenario object with .scenes
      if (first.scenes && typeof first.scenes === 'object') {
        initScenario = first;
      }

      // New format: flat array of steps → auto-build scene graph
      else if (first.line || first.speaker) {
        console.log('PretextingSimulator: Building dynamic scene graph from flat steps');

        const scenes = {};
        let initialSceneId = null;

        // Build scenes from steps
        scenarios.forEach((step, index) => {
          const sceneId = step.sceneId || `step_${index}`;
          if (!initialSceneId) initialSceneId = sceneId;

          scenes[sceneId] = {
            speaker: step.speaker || 'Caller',
            line: step.line || '',
            choices: step.choices || [], // Use if provided
          };
        });

        // Auto-link choices if not provided
        const sceneIds = Object.keys(scenes);
        sceneIds.forEach((sceneId, index) => {
          if (index < sceneIds.length - 1) {
            const nextId = sceneIds[index + 1];

            // If scene already has choices (from backend), skip
            if (scenes[sceneId].choices && scenes[sceneId].choices.length > 0) return;

            // Auto-generate safe vs unsafe choices
            scenes[sceneId].choices = [
              {
                text: "This seems suspicious. I'm hanging up.",
                nextSceneId: 'safe_hangup'
              },
              {
                text: "Okay, what do I need to do?",
                nextSceneId: nextId
              }
            ];
          }
        });

        // Add universal safe end state
        scenes['safe_hangup'] = {
          speaker: 'You',
          line: "I'm ending this call.",
          isEndState: true,
          isCorrect: true,
          explanation: "Correct! The safest response to any unsolicited urgent call is to hang up and verify independently through official channels.",
          indicators: ["Unsolicited call", "Urgency", "Authority impersonation", "Refusal to allow verification"]
        };

        // Mark final scene as end (unless it already has isEndState)
        const lastSceneId = sceneIds[sceneIds.length - 1];
        if (!scenes[lastSceneId].isEndState) {
          scenes[lastSceneId].isEndState = true;
          scenes[lastSceneId].isCorrect = false;
          scenes[lastSceneId].explanation = "You continued engaging with a suspicious caller. Always verify through official channels.";
        }

        initScenario = {
          title: challenge?.title || 'Phone Scam Simulation',
          description: challenge?.description || 'Handle an incoming pretexting attack',
          initialSceneId,
          arenaId: challenge?.arenaId || 'social_engineering',
          scenes
        };
      }
    }

    // Final fallback
    if (!initScenario) {
      initScenario = getDefaultScenario();
    }

    setScenario(initScenario);
    setCurrentScene(initScenario.scenes[initScenario.initialSceneId]);

    // Reset all state
    setHistory([]);
    setIsComplete(false);
    setCallState('ringing');
    setTimer(0);
    setFinalFeedback(null);
    setShowFeedbackModal(false);
  }, [scenarios, challenge]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  useEffect(() => {
    if (callState === 'active') {
      const id = setInterval(() => setTimer(t => t + 1), 1000);
      return () => clearInterval(id);
    }
  }, [callState]);

  // --- Ringtone ---
  const stopRingtone = useCallback(() => {
    try {
      ringTimeoutsRef.current.forEach(clearTimeout);
      ringTimeoutsRef.current = [];
      if (ringIntervalRef.current) clearInterval(ringIntervalRef.current);
      if (oscillatorRef.current) oscillatorRef.current.stop();
      if (gainRef.current) gainRef.current.disconnect();
      if (audioCtxRef.current) audioCtxRef.current.close();
    } catch (e) { }
    oscillatorRef.current = gainRef.current = audioCtxRef.current = ringIntervalRef.current = null;
  }, []);

  const startRingtone = useCallback(() => {
    if (audioCtxRef.current) return;
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;

      const gain = ctx.createGain();
      gain.gain.value = 0;
      gain.connect(ctx.destination);
      gainRef.current = gain;

      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 540;
      osc.connect(gain);
      osc.start();
      oscillatorRef.current = osc;

      const playPattern = () => {
        const now = ctx.currentTime;
        gain.gain.cancelScheduledValues(now);
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.9, now + 0.02);
        ringTimeoutsRef.current.push(setTimeout(() => gain.gain.linearRampToValueAtTime(0, now + 0.92), 900));
        ringTimeoutsRef.current.push(setTimeout(() => {
          gain.gain.linearRampToValueAtTime(0.9, ctx.currentTime + 0.02);
          ringTimeoutsRef.current.push(setTimeout(() => gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.02), 900));
        }, 1500));
      };
      playPattern();
      ringIntervalRef.current = setInterval(playPattern, 2000);
    } catch (e) { stopRingtone(); }
  }, [stopRingtone]);

  useEffect(() => {
    callState === 'ringing' ? startRingtone() : stopRingtone();
  }, [callState, startRingtone, stopRingtone]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRingtone();
      window.speechSynthesis.cancel();
    };
  }, [stopRingtone]);

  // --- Voice Unlock & Speak ---
  const unlockVoice = async () => {
    if (voiceUnlocked || !voiceSupported) return;
    const dummy = new SpeechSynthesisUtterance(' ');
    dummy.volume = 0.01;
    window.speechSynthesis.speak(dummy);
    await new Promise(r => setTimeout(r, 200));
    setVoiceUnlocked(true);
  };

  const speakQueue = useRef([]);
  const isSpeaking = useRef(false);

  const processQueue = () => {
    if (isSpeaking.current || speakQueue.current.length === 0) return;
    isSpeaking.current = true;

    const { text, voice, rate, pitch } = speakQueue.current.shift();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = voice;
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.volume = 1.0;

    utterance.onend = utterance.onerror = () => {
      isSpeaking.current = false;
      processQueue();
    };

    window.speechSynthesis.speak(utterance);
  };

  const speak = (text, voice, rate = 1.0, pitch = 1.0) => {
    if (!voiceSupported || !voice || !text) return;

    // Unlock on first real speech
    if (!voiceUnlocked) {
      const dummy = new SpeechSynthesisUtterance('');
      dummy.volume = 0;
      window.speechSynthesis.speak(dummy);
      setVoiceUnlocked(true);
    }

    speakQueue.current.push({
      text: text.trim() + (/[.!?]$/.test(text) ? '' : '.'),
      voice,
      rate,
      pitch,
    });
    processQueue();
  };

  const speakAttacker = (text) => speak(text, attackerVoice, 1.5, 0.82);
  const speakUser = (text) => speak(text, userVoice, 1.4, 1.22);

  // --- Core Handlers ---
  const handleAcceptCall = async () => {
    setCallState('active');
    setHistory([
      { type: 'system', line: `Call started from "${currentScene.speaker}"` },
      { type: 'attacker', speaker: currentScene.speaker, line: currentScene.line }
    ]);
    stopRingtone();

    if (voiceSupported) {
      await unlockVoice();
      setTimeout(() => speakAttacker(currentScene.line), 400); // First line spoken
    }
  };

  const handleRejectCall = () => {
    stopRingtone();
    setIsComplete(true);
    setCallState('ended');
    setHistory([{ type: 'system', line: 'You rejected the incoming call.' }]);

    const feedback = {
      isCorrect: true,
      explanation: "Correct! By rejecting an unsolicited call from an unknown number, you avoided a potential pretexting attack.",
      indicators: []
    };
    setFinalFeedback(feedback);
    setShowFeedbackModal(true);
    onComplete?.({ correct: 1, incorrect: 0, total: 1 });
  };

  const handleChoice = async (choice) => {
    if (isComplete) return;

    // Add user's choice to chat
    setHistory(h => [...h, { type: 'user', speaker: 'You', line: choice.text }]);

    const nextScene = scenario.scenes[choice.nextSceneId];
    if (!nextScene) return;

    // Add attacker's reply
    setHistory(h => [...h, { type: 'attacker', speaker: nextScene.speaker, line: nextScene.line }]);
    setCurrentScene(nextScene);

    // Speak user's line (exactly what they clicked)
    if (voiceSupported) {
      if (!voiceUnlocked) await unlockVoice();
      speakUser(choice.text);
      setTimeout(() => speakAttacker(nextScene.line), 800);
    }

    // End game immediately if terminal scene
    if (nextScene.isEndState) {
      setIsComplete(true);
      setCallState('ended');

      const feedback = {
        isCorrect: nextScene.isCorrect,
        explanation: nextScene.explanation,
        indicators: nextScene.indicators || []
      };
      setFinalFeedback(feedback);
      setShowFeedbackModal(true);

      // Report result to backend
      onComplete?.({
        correct: nextScene.isCorrect ? 1 : 0,
        incorrect: nextScene.isCorrect ? 0 : 1,
        total: 1
      });
    }
  };

  const handleReplayAttacker = () => {
    if (voiceUnlocked && currentScene?.line) speakAttacker(currentScene.line);
  };

  const handleCloseFeedbackModal = () => setShowFeedbackModal(false);
  const handleLearnMore = () => {
    if (challenge?.metadata) {
      window.open(challenge.metadata, '_blank', 'noopener,noreferrer');
    } else {
      const id = scenario?.arenaId || 'phishing_social_engineering';
      navigate(`/learning/module/${id}?topic=${id}`);
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  // --- Render ---
  const renderRingingScreen = () => (
    <div className="flex flex-col items-center justify-between h-full bg-gray-800 text-white p-8">
      <div className="text-center mt-10">
        <div className="w-24 h-24 bg-gray-700 rounded-full mx-auto flex items-center justify-center">
          <User size={48} />
        </div>
        <h2 className="text-3xl font-semibold mt-6">Incoming Call</h2>
        <p className="text-lg text-gray-400 mt-1 animate-pulse">calling...</p>
      </div>
      <div className="w-full flex items-center justify-center gap-16">
        <button onClick={handleRejectCall} className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center hover:scale-105 transition">
          <PhoneOff size={32} className="text-white" />
        </button>
        <button onClick={handleAcceptCall} className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center hover:scale-105 transition">
          <Phone size={32} className="text-white" />
        </button>
      </div>
    </div>
  );

  const renderActiveCallScreen = () => (
    <div className="flex flex-col h-full bg-gray-100">
      <div className="text-center p-4 bg-white border-b border-gray-200 shadow-sm flex items-center justify-between gap-4">
        <div>
          <h3 className="font-semibold text-gray-800">{currentScene?.speaker || 'Caller'}</h3>
          <p className={`text-sm ${callState === 'ended' ? 'text-red-500' : 'text-gray-500'}`}>
            {callState === 'ended' ? 'Call Ended' : formatTime(timer)}
          </p>
        </div>
        <button onClick={handleReplayAttacker} className="px-3 py-2 bg-blue-50 text-blue-700 rounded-md text-sm">Replay</button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {history.map((entry, i) => {
          if (entry.type === 'system')
            return <div key={i} className="text-center text-xs text-gray-400 uppercase tracking-wider">{entry.line}</div>;
          const isUser = entry.type === 'user';
          return (
            <div key={i} className={`flex items-end gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}>
              {!isUser && <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center"><User size={16} className="text-gray-600" /></div>}
              <div className={`max-w-xs md:max-w-md p-3 rounded-2xl ${isUser ? 'bg-blue-500 text-white rounded-br-lg' : 'bg-white text-gray-800 rounded-bl-lg'}`}>
                <p>{entry.line}</p>
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      {!isComplete && currentScene?.choices?.length > 0 && (
        <div className="p-3 border-t border-gray-200 bg-white">
          <div className="grid grid-cols-1 gap-2">
            {currentScene.choices.map((c, i) => (
              <button
                key={i}
                onClick={() => handleChoice(c)}
                className="w-full text-center p-3 text-blue-600 font-medium bg-blue-50 hover:bg-blue-100 rounded-lg transition text-sm"
              >
                {c.text}
              </button>
            ))}
          </div>
        </div>
      )}

      {isComplete && <div className="p-4 text-center text-sm text-gray-500 bg-gray-200">Call has ended.</div>}
    </div>
  );

  const renderFeedbackModal = () => {
    if (!showFeedbackModal || !finalFeedback) return null;
    const { isCorrect, explanation, indicators } = finalFeedback;

    return (
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full p-8 m-4">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              {isCorrect ? <CheckCircle size={28} className="text-green-600" /> : <AlertTriangle size={28} className="text-red-600" />}
              <h2 className={`text-2xl font-bold ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                {isCorrect ? 'Correct Decision!' : 'Incorrect Decision!'}
              </h2>
            </div>
            <button onClick={handleCloseFeedbackModal} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
          </div>

          <div className="text-gray-700 space-y-4">
            <p className="text-base">{explanation}</p>
            {indicators?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="font-bold text-yellow-800 flex items-center">
                  <AlertTriangle size={16} className="mr-2" /> Key Indicators Found:
                </h3>
                <ul className="list-disc list-inside space-y-1.5 mt-2 text-gray-700 text-sm pl-2">
                  {indicators.map((ind, i) => <li key={i}>{ind}</li>)}
                </ul>
              </div>
            )}
          </div>

          <div className="mt-8 flex justify-end gap-4">
            <button onClick={handleLearnMore} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition">
              <BookOpen size={16} /> Learn More
            </button>
            <button onClick={handleCloseFeedbackModal} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition">
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative h-full max-w-sm mx-auto bg-white rounded-2xl border-8 border-black shadow-2xl overflow-hidden font-sans">
      <div className="h-full">
        {callState === 'ringing' ? renderRingingScreen() : renderActiveCallScreen()}
      </div>
      {showFeedbackModal && renderFeedbackModal()}
    </div>
  );
};

export default PretextingSimulator;